import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import { Target, Plus, Trash2, ChevronDown, ChevronRight, Sparkles, FolderKanban, CheckCircle2, Circle } from "lucide-react";

const GOAL_STATUS_LABELS = { actif: "Actif", termine: "Terminé", abandonne: "Abandonné" };
const TASK_NEXT = { a_faire: "en_cours", en_cours: "termine", termine: "a_faire" };

function ProjectBlock({ project, onChanged }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [newTask, setNewTask] = useState("");
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);
  const [selected, setSelected] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${project.project_id}`);
      setDetail(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [project.project_id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;
    setError("");
    try {
      await api.post(`/projects/${project.project_id}/tasks`, { title: newTask });
      setNewTask("");
      await load();
      onChanged();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const cycleTask = async (task) => {
    setError("");
    try {
      await api.patch(`/tasks/${task.task_id}`, { status: TASK_NEXT[task.status] });
      await load();
      onChanged();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      await load();
      onChanged();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    setError("");
    setDraft(null);
    try {
      const res = await api.post(`/projects/${project.project_id}/suggest-tasks`);
      setDraft(res.data.tasks);
      setSelected(res.data.tasks.map((_, i) => i));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSuggesting(false);
    }
  };

  const addSelected = async () => {
    if (!draft || selected.length === 0) return;
    setError("");
    try {
      await api.post(`/projects/${project.project_id}/tasks/bulk`, {
        titles: selected.map((i) => draft[i]),
      });
      setDraft(null);
      setSelected([]);
      await load();
      onChanged();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const tasks = detail?.tasks || [];

  return (
    <div data-testid={`project-${project.project_id}`} className="border border-slate-100 rounded-lg">
      <button
        type="button"
        data-testid={`project-toggle-${project.project_id}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 rounded-lg transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
          <FolderKanban className="w-4 h-4 text-brand-blue shrink-0" />
          <span className="text-sm font-medium text-brand-navy truncate">{project.title}</span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-slate-400">{project.tasks_done}/{project.tasks_total}</span>
          <span className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <span data-testid={`project-progress-${project.project_id}`} className="block h-full bg-brand-turquoise rounded-full" style={{ width: `${project.progress}%` }} />
          </span>
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          {error && <p data-testid={`project-error-${project.project_id}`} className="text-xs text-red-600">{error}</p>}
          {tasks.map((t) => (
            <div key={t.task_id} data-testid={`task-${t.task_id}`} className="flex items-center gap-2 group">
              <button
                type="button"
                data-testid={`task-toggle-${t.task_id}`}
                onClick={() => cycleTask(t)}
                title={`Statut : ${t.status}`}
                className="shrink-0"
              >
                {t.status === "termine" ? (
                  <CheckCircle2 className="w-5 h-5 text-brand-turquoise" />
                ) : t.status === "en_cours" ? (
                  <Circle className="w-5 h-5 text-brand-orange fill-brand-orange/20" />
                ) : (
                  <Circle className="w-5 h-5 text-slate-300" />
                )}
              </button>
              <span className={`text-sm flex-1 ${t.status === "termine" ? "line-through text-slate-400" : "text-slate-700"}`}>
                {t.title}
                {t.source === "ia" && <span className="ml-2 text-[10px] font-medium bg-brand-sky text-brand-navy rounded-full px-2 py-0.5">IA</span>}
              </span>
              <button
                type="button"
                data-testid={`task-delete-${t.task_id}`}
                onClick={() => deleteTask(t.task_id)}
                className="text-slate-200 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {tasks.length === 0 && <p className="text-xs text-slate-400">Aucune tâche. Ajoutez-en une ou laissez l'IA proposer un plan.</p>}

          <form onSubmit={addTask} className="flex gap-2 pt-1">
            <input
              data-testid={`task-input-${project.project_id}`}
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              placeholder="Nouvelle tâche…"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
            />
            <button data-testid={`task-add-${project.project_id}`} type="submit" className="text-brand-turquoise hover:text-brand-navy transition-colors">
              <Plus className="w-4 h-4" />
            </button>
          </form>

          <button
            type="button"
            data-testid={`suggest-tasks-${project.project_id}`}
            onClick={suggest}
            disabled={suggesting}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-orange hover:text-brand-navy transition-colors disabled:opacity-50"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {suggesting ? "Analyse IA…" : "Suggérer des tâches (IA)"}
          </button>

          {draft && (
            <div data-testid={`tasks-draft-${project.project_id}`} className="bg-brand-sky/50 border border-brand-blue/20 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-brand-blue uppercase tracking-wide">
                Brouillon IA (mode SUGGEST) — cochez puis ajoutez, rien n'est créé automatiquement
              </p>
              {draft.map((t, i) => (
                <label key={i} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`draft-task-${project.project_id}-${i}`}
                    checked={selected.includes(i)}
                    onChange={(e) =>
                      setSelected(e.target.checked ? [...selected, i] : selected.filter((s) => s !== i))
                    }
                    className="w-4 h-4 accent-brand-turquoise"
                  />
                  {t}
                </label>
              ))}
              <button
                type="button"
                data-testid={`draft-add-${project.project_id}`}
                onClick={addSelected}
                disabled={selected.length === 0}
                className="text-xs font-semibold text-white bg-brand-navy rounded-lg px-4 py-2 hover:bg-brand-blue transition-colors disabled:opacity-50"
              >
                Ajouter la sélection ({selected.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", target_date: "" });
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/goals");
      setGoals(res.data);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createGoal = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post("/goals", { ...form, target_date: form.target_date || null });
      setForm({ title: "", description: "", target_date: "" });
      setShowForm(false);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setGoalStatus = async (goalId, status) => {
    try {
      await api.patch(`/goals/${goalId}`, { status });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const deleteGoal = async (goalId) => {
    try {
      await api.delete(`/goals/${goalId}`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const addProject = async (goalId, title) => {
    if (!title.trim()) return;
    try {
      await api.post(`/goals/${goalId}/projects`, { title });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const inputCls =
    "w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent";

  return (
    <div data-testid="goals-page" className="min-h-screen bg-slate-50">
      <PublicNav />
      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-brand-navy flex items-center gap-2">
            <Target className="w-5 h-5 text-brand-orange" />
            Objectifs & Projets
          </h1>
          <button
            data-testid="new-goal-button"
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-1.5 bg-brand-turquoise text-white text-sm font-semibold rounded-lg px-4 py-2.5 hover:bg-brand-navy transition-colors duration-200"
          >
            <Plus className="w-4 h-4" />
            Nouvel objectif
          </button>
        </div>

        {error && <div data-testid="goals-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>}

        {showForm && (
          <form data-testid="goal-form" onSubmit={createGoal} className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
            <input data-testid="goal-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre de l'objectif (ex. Décrocher un emploi de soudeur au Canada)" className={inputCls} />
            <textarea data-testid="goal-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optionnel)" rows={2} className={inputCls} />
            <div className="flex items-center gap-3">
              <input data-testid="goal-date-input" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className={`${inputCls} w-auto`} />
              <button data-testid="goal-submit-button" type="submit" disabled={saving} className="bg-brand-navy text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-brand-blue transition-colors disabled:opacity-50">
                {saving ? "Création…" : "Créer l'objectif"}
              </button>
            </div>
          </form>
        )}

        <div className="space-y-4">
          {goals.map((g) => (
            <GoalCard
              key={g.goal_id}
              goal={g}
              onChanged={load}
              onStatus={setGoalStatus}
              onDelete={deleteGoal}
              onAddProject={addProject}
            />
          ))}
          {goals.length === 0 && (
            <p data-testid="goals-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-10 text-center">
              Aucun objectif. Définissez votre premier objectif — l'IA vous proposera un plan d'action.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}

function GoalCard({ goal, onChanged, onStatus, onDelete, onAddProject }) {
  const [newProject, setNewProject] = useState("");

  const submitProject = (e) => {
    e.preventDefault();
    onAddProject(goal.goal_id, newProject);
    setNewProject("");
  };

  return (
    <section data-testid={`goal-${goal.goal_id}`} className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className="font-semibold text-brand-navy">{goal.title}</h2>
          {goal.description && <p className="text-xs text-slate-500 mt-0.5">{goal.description}</p>}
          <p className="text-xs text-slate-400 mt-1">
            {goal.target_date ? `Cible : ${new Date(goal.target_date).toLocaleDateString("fr-CA")} · ` : ""}
            {goal.projects.length} projet(s)
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select
            data-testid={`goal-status-${goal.goal_id}`}
            value={goal.status}
            onChange={(e) => onStatus(goal.goal_id, e.target.value)}
            className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white"
          >
            {Object.entries(GOAL_STATUS_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <button data-testid={`goal-delete-${goal.goal_id}`} onClick={() => onDelete(goal.goal_id)} className="text-slate-300 hover:text-red-500 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <span data-testid={`goal-progress-${goal.goal_id}`} className="block h-full bg-brand-orange rounded-full transition-all duration-300" style={{ width: `${goal.progress}%` }} />
        </span>
        <span data-testid={`goal-progress-label-${goal.goal_id}`} className="text-xs font-semibold text-brand-navy w-10 text-right">{goal.progress}%</span>
      </div>

      <div className="space-y-2">
        {goal.projects.map((p) => (
          <ProjectBlock key={p.project_id} project={p} onChanged={onChanged} />
        ))}
      </div>

      <form onSubmit={submitProject} className="flex gap-2 mt-3">
        <input
          data-testid={`project-input-${goal.goal_id}`}
          value={newProject}
          onChange={(e) => setNewProject(e.target.value)}
          placeholder="Nouveau projet dans cet objectif…"
          className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
        />
        <button data-testid={`project-add-${goal.goal_id}`} type="submit" className="text-brand-turquoise hover:text-brand-navy transition-colors">
          <Plus className="w-4 h-4" />
        </button>
      </form>
    </section>
  );
}
